import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticValve } from "./realistic";

/**
 * ValveNode – Centrifugal valve visualization.
 * Supports:
 * - Realistic 3D SCADA design with rotating handwheel & flow indicator
 * - Open / close state (tied to isRunning)
 * - Glassmorphism design alternative
 * - Classic industrial SVG layout
 */
const ValveNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticValve
              liveValue={liveValue}
              unit={unit || "%"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const isOpen = isRunning;
        const stateColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";
        const bodyFill = isOpen ? `${color}20` : `${color}45`;
        const flowDuration = isOpen ? `${1.5 / (speedScale || 1)}s` : "0s";

        const isNewDesign = designType === "svg" || designType === "glassmorphism";

        return (
          <svg
            width="70"
            height="60"
            viewBox="0 0 70 60"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              transition: "all 0.3s ease",
            }}
          >
            {isNewDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  <linearGradient id="valveGlassGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="valveActiveGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={color} stopOpacity="1" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.5" />
                  </linearGradient>
                  <filter id="valveGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Flow indicator (animated when open) */}
                {isOpen && (
                  <line
                    x1="5"
                    y1="30"
                    x2="65"
                    y2="30"
                    stroke={stateColor}
                    strokeWidth="2.5"
                    strokeDasharray="4 6"
                    opacity="0.6"
                    style={{ animation: `live-valve-flow ${flowDuration} linear infinite` }}
                  />
                )}

                {/* Left triangle (inlet) */}
                <polygon
                  points="5,10 35,30 5,50"
                  fill="url(#valveGlassGrad)"
                  stroke={isOpen ? "url(#valveActiveGrad)" : "#475569"}
                  strokeWidth="2.5"
                  strokeLinejoin="round"
                  style={{
                    filter: isOpen ? "url(#valveGlow)" : "none",
                  }}
                />

                {/* Right triangle (outlet) */}
                <polygon
                  points="65,10 35,30 65,50"
                  fill="url(#valveGlassGrad)"
                  stroke={isOpen ? "url(#valveActiveGrad)" : "#475569"}
                  strokeWidth="2.5"
                  strokeLinejoin="round"
                  style={{
                    filter: isOpen ? "url(#valveGlow)" : "none",
                  }}
                />

                {/* Center axis / stem */}
                <line x1="35" y1="5" x2="35" y2="30" stroke={color} strokeWidth="2.5" strokeLinecap="round" />

                {/* Handwheel */}
                <g style={{
                  transformOrigin: "35px 5px",
                }}>
                  <circle cx="35" cy="5" r="5" fill="none" stroke={color} strokeWidth="2.5" />
                  <line x1="31" y1="5" x2="39" y2="5" stroke={color} strokeWidth="1.5" />
                  <line x1="35" y1="1" x2="35" y2="9" stroke={color} strokeWidth="1.5" />
                </g>

                {/* Center status indicator */}
                <circle cx="35" cy="30" r="6" fill={stateColor} style={{ filter: "drop-shadow(0px 0px 4px rgba(0,0,0,0.3))" }} />
                <circle cx="35" cy="30" r="6" fill="none" stroke="#ffffff" strokeWidth="1.5" opacity="0.8" />

                {/* State label */}
                <text
                  x="35"
                  y="57"
                  textAnchor="middle"
                  fontSize="8.5"
                  fontWeight="800"
                  fill={stateColor}
                  style={{ letterSpacing: 0.5 }}
                >
                  {statusColor === "green" ? "OPEN" : statusColor === "red" ? "CLOSED" : "OFFLINE"}
                </text>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* Flow indicator (animated when open) */}
                {isOpen && (
                  <line
                    x1="5"
                    y1="30"
                    x2="65"
                    y2="30"
                    stroke={stateColor}
                    strokeWidth="2"
                    strokeDasharray="4 6"
                    opacity="0.4"
                    style={{ animation: `live-valve-flow ${flowDuration} linear infinite` }}
                  />
                )}

                {/* Left triangle (inlet) */}
                <polygon
                  points="5,10 35,30 5,50"
                  fill={bodyFill}
                  stroke={color}
                  strokeWidth="2"
                  strokeLinejoin="round"
                />

                {/* Right triangle (outlet) */}
                <polygon
                  points="65,10 35,30 65,50"
                  fill={bodyFill}
                  stroke={color}
                  strokeWidth="2"
                  strokeLinejoin="round"
                />

                {/* Center axis / stem */}
                <line x1="35" y1="5" x2="35" y2="30" stroke={color} strokeWidth="2.5" strokeLinecap="round" />

                {/* Handwheel */}
                <g style={{
                  transformOrigin: "35px 5px",
                  animation: isOpen ? "none" : "live-valve-close 0.5s ease-out",
                }}>
                  <circle cx="35" cy="5" r="5" fill="none" stroke={color} strokeWidth="2" />
                  <line x1="31" y1="5" x2="39" y2="5" stroke={color} strokeWidth="1.5" />
                  <line x1="35" y1="1" x2="35" y2="9" stroke={color} strokeWidth="1.5" />
                </g>

                {/* Center status indicator */}
                <circle cx="35" cy="30" r="5" fill={stateColor} />
                <circle cx="35" cy="30" r="5" fill="none" stroke="#fff" strokeWidth="1" opacity="0.5" />

                {/* State label */}
                <text
                  x="35"
                  y="56"
                  textAnchor="middle"
                  fontSize="8"
                  fontWeight="600"
                  fill={stateColor}
                >
                  {statusColor === "green" ? "OPEN" : statusColor === "red" ? "CLOSED" : "OFFLINE"}
                </text>

                {/* Inlet pipe stub */}
                <rect x="0" y="27" width="5" height="6" fill={color} rx="1" />

                {/* Outlet pipe stub */}
                <rect x="65" y="27" width="5" height="6" fill={color} rx="1" />
              </>
            )}
          </svg>
        );
      }}
    </BaseNode>
  );
};

ValveNode.propTypes = {
  data: PropTypes.object,
  selected: PropTypes.bool,
  id: PropTypes.string,
};

export default ValveNode;
